import { useEffect, useState } from "react";
import { CarFront, Users, Fuel, Settings2, X, MessageCircle, Truck } from "lucide-react";
import { useLang } from "@/i18n";
import { API, waLink, submitRequest } from "@/config";
import { Field, Input, Textarea, Gdpr, Honeypot, SuccessBox, SubmitBtn, useFormState, todayISO } from "@/components/FormBits";
import { toast } from "sonner";

function VehicleCard({ v, onRequest }) {
  const { t } = useLang();
  return (
    <div data-testid={`vehicle-card-${v.id}`} className="card-dark group hover:border-[#E11D48]/50 transition-colors duration-300">
      <div className="relative h-48 overflow-hidden">
        {v.image_url ? (
          <img src={v.image_url.startsWith("/") ? `${API.replace("/api", "")}${v.image_url}` : v.image_url} alt={`${v.brand} ${v.model}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-zinc-800 flex items-center justify-center"><CarFront size={40} className="text-zinc-600" /></div>
        )}
        <span data-testid={`vehicle-status-${v.id}`}
          className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1 ${v.available ? "bg-[#22C55E]/90 text-white" : "bg-zinc-700/90 text-zinc-300"}`}>
          {v.available ? t("rental.available") : t("rental.unavailable")}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-heading font-bold text-lg text-white">{v.brand} {v.model}</h3>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-zinc-400">
          <span className="flex items-center gap-1"><Settings2 size={12} className="text-[#E11D48]" />{v.year}</span>
          <span className="flex items-center gap-1"><Fuel size={12} className="text-[#E11D48]" />{v.fuel}</span>
          <span className="flex items-center gap-1"><Users size={12} className="text-[#E11D48]" />{v.seats} {t("rental.seats")}</span>
          <span className="col-span-3">{v.transmission}</span>
        </div>
        <div className="mt-4 flex items-end gap-4 border-t border-zinc-800 pt-4">
          <div>
            <span className="font-heading text-2xl font-extrabold text-white">€{v.price_day}</span>
            <span className="text-xs text-zinc-500">{t("rental.day")}</span>
          </div>
          {v.price_week != null && <div className="text-sm text-zinc-400">€{v.price_week}<span className="text-xs text-zinc-500">{t("rental.week")}</span></div>}
          {v.price_month != null && <div className="text-sm text-zinc-400">€{v.price_month}<span className="text-xs text-zinc-500">{t("rental.month")}</span></div>}
        </div>
        <p className="mt-2 text-xs text-zinc-500">{t("rental.deposit")}: €{v.deposit}</p>
        {v.features && <p className="mt-2 text-xs text-zinc-400">{v.features}</p>}
        <button
          onClick={() => onRequest(v)}
          disabled={!v.available}
          data-testid={`vehicle-request-${v.id}`}
          className="btn-red w-full mt-4 !py-2.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
          {v.category === "van" ? t("rental.reserve") : t("rental.request")}
        </button>
      </div>
    </div>
  );
}

function RentalModal({ vehicle, onClose }) {
  const { t } = useLang();
  const [data, set, setData] = useFormState({ name: "", phone: "", email: "", start: "", end: "", vehicle: `${vehicle.brand} ${vehicle.model}`, delivery: "pickup", deliveryAddress: "", remarks: "", website: "", gdpr: false });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitRequest("rental", data); setDone(true); }
    catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="rental-modal">
      <div className="card-dark w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto rounded-b-none sm:rounded-b-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-xl text-white">{t("rental.modalTitle")}</h3>
          <button onClick={onClose} data-testid="rental-modal-close" className="p-1.5 text-zinc-400 hover:text-white"><X size={20} /></button>
        </div>
        {done ? <SuccessBox message={t("rental.success")} testid="rental-success" /> : (
          <form onSubmit={submit} className="grid gap-4" data-testid="rental-form">
            <Honeypot value={data.website} onChange={set("website")} />
            <Field label={t("rental.chosen")}><Input value={data.vehicle} readOnly className="input-dark opacity-70" data-testid="r-vehicle" /></Field>
            <Field label={t("rental.deliveryLabel")} required>
              <div className="grid grid-cols-2 gap-2">
                {[["pickup", t("rental.pickup")], ["delivery", t("rental.delivery")]].map(([v, label]) => (
                  <button type="button" key={v} onClick={() => setData((d) => ({ ...d, delivery: v }))} data-testid={`r-delivery-${v}`}
                    className={`py-2.5 px-2 rounded-lg border text-xs font-semibold transition-colors ${data.delivery === v ? "border-[#E11D48] bg-[#E11D48]/10 text-white" : "border-zinc-700 text-zinc-400"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            {data.delivery === "delivery" && (
              <>
                <div className="flex items-start gap-2.5 card-dark border-[#D97706]/40 p-3.5 text-xs text-zinc-300" data-testid="r-delivery-info">
                  <Truck size={15} className="text-[#D97706] shrink-0 mt-0.5" />
                  {t("rental.deliveryInfo")}
                </div>
                <Field label={t("rental.deliveryAddress")} required><Input required value={data.deliveryAddress} onChange={set("deliveryAddress")} data-testid="r-delivery-address" /></Field>
              </>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("rental.start")} required><Input required type="date" min={todayISO()} value={data.start} onChange={set("start")} data-testid="r-start" /></Field>
              <Field label={t("rental.end")} required><Input required type="date" min={data.start || todayISO()} value={data.end} onChange={set("end")} data-testid="r-end" /></Field>
            </div>
            <Field label={t("form.name")} required><Input required value={data.name} onChange={set("name")} data-testid="r-name" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("form.phone")} required><Input required type="tel" value={data.phone} onChange={set("phone")} data-testid="r-phone" /></Field>
              <Field label={t("form.email")} required><Input required type="email" value={data.email} onChange={set("email")} data-testid="r-email" /></Field>
            </div>
            <Field label={t("form.remarks")}><Textarea value={data.remarks} onChange={set("remarks")} data-testid="r-remarks" /></Field>
            <Gdpr checked={data.gdpr} onChange={(v) => set("gdpr")({ target: { value: v } })} />
            <SubmitBtn loading={loading} label={t("rental.send")} testid="r-submit" />
            <p className="text-xs text-zinc-500 text-center" data-testid="r-deposit-note">{t("form.depositNote")}</p>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Verhuur() {
  const { t } = useLang();
  const [vehicles, setVehicles] = useState([]);
  const [tab, setTab] = useState("car");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/vehicles`).then((r) => r.json()).then(setVehicles).catch(() => {});
  }, []);

  const filtered = vehicles.filter((v) => v.category === tab);

  return (
    <div className="container-x py-14" data-testid="verhuur-page">
      <p className="overline-red">Milano Worx Rental</p>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
        {tab === "car" ? t("rental.title") : t("rental.vanTitle")}
      </h1>
      <p className="mt-3 text-zinc-400 max-w-2xl">{t("rental.sub")}</p>

      <div className="mt-8 flex gap-2" data-testid="rental-fleet-filter">
        {[["car", t("rental.cars")], ["van", t("rental.vans")]].map(([v, label]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`rental-tab-${v}`}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${tab === v ? "bg-[#E11D48] border-[#E11D48] text-white" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((v) => <VehicleCard key={v.id} v={v} onRequest={setSelected} />)}
        {filtered.length === 0 && <p className="text-zinc-500 col-span-full py-10 text-center">—</p>}
      </div>

      <div className="mt-10 card-dark border-[#D97706]/40 p-5 flex items-start gap-4" data-testid="delivery-info-banner">
        <Truck size={22} className="text-[#D97706] shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-white">{t("rental.delivery")}</h3>
          <p className="text-sm text-zinc-400 mt-1">{t("rental.deliveryInfo")}</p>
        </div>
      </div>

      <div className="mt-6 card-dark p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-sm text-zinc-400 max-w-xl">{t("rental.sub")}</p>
        <a href={waLink(t("wa.rental"))} target="_blank" rel="noreferrer" className="btn-wa shrink-0" data-testid="rental-whatsapp-cta">
          <MessageCircle size={18} />WhatsApp
        </a>
      </div>

      {selected && <RentalModal vehicle={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
